import React, { createContext, useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

export const ModalsContext = createContext();

export const useAdsModal = () => {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useAdsModal debe usarse dentro de ModalProvider');
  }
  return context;
};

// Modal types
export const MODAL_TYPES = {
  NONE: 'none',
  UPSELL: 'upsell',
  NO_ADS_CONNECTED: 'no_ads_connected',
  CONNECT_GOOGLE_ADS_ACCOUNT: 'connect_google_ads_account',
  CONNECT_META_ADS_ACCOUNT: 'connect_meta_ads_account',
  PLANS: 'plans',
  CONNECTIONS: 'connections',
  UPDATE_BUSINESS_DATA: 'update_business_data',
  CREATE_OBJECTIVES_SETUP: 'create_objectives_setup'
};

function isConnectAccountPickerModal(modal) {
  return (
    modal === MODAL_TYPES.CONNECT_GOOGLE_ADS_ACCOUNT ||
    modal === MODAL_TYPES.CONNECT_META_ADS_ACCOUNT
  );
}

/** Modal de objetivos con flag obligatorio (independiente del ref de “ya generó”). */
function isMandatoryObjectivesSetupModal(modal, objectivesConfig) {
  return (
    modal === MODAL_TYPES.CREATE_OBJECTIVES_SETUP &&
    Boolean(objectivesConfig?.mandatoryUntilGenerated)
  );
}

/** No cerrar el modal global al cambiar de ruta: pickers post-OAuth o setup obligatorio antes de liberar guard. */
export function shouldPreserveModalOnRouteChange(modal, objectivesConfig, routeGuardReleased) {
  if (isConnectAccountPickerModal(modal)) return true;
  if (isMandatoryObjectivesSetupModal(modal, objectivesConfig) && !routeGuardReleased) {
    return true;
  }
  return false;
}

export const AdsModalProvider = ({ children }) => {
  const [currentModal, setCurrentModal] = useState(MODAL_TYPES.NONE);
  const [upsellModalConfig, setUpsellModalConfig] = useState({});
  const [objectivesSetupModalConfig, setObjectivesSetupModalConfig] = useState({});
  const [plansModalMessage, setPlansModalMessage] = useState('');
  /** Cuando true, el selector de marcas (FloatingBrandSelector) se muestra con blur detrás del pop-up de optimizaciones */
  const [isOptimizationsListModalOpen, setIsOptimizationsListModalOpen] = useState(false);
  /** Modal de detalle de una optimización (ej. "Dividir mejores vendedores") */
  const [isOptimizationDetailModalOpen, setIsOptimizationDetailModalOpen] = useState(false);
  const location = useLocation();

  /**
   * Route-change guard for CREATE_OBJECTIVES_SETUP + mandatoryUntilGenerated.
   * - Opening that modal with mandatoryUntilGenerated: true sets this ref to false (see openModal).
   * - closeModal() and non-preserved route changes reset it to true.
   * - shouldPreserveModalOnRouteChange reads it so navigation does not dismiss the modal mid-flow.
   * No separate "release" API: closing the modal or finishing the flow via closeModal() clears the guard.
   */
  const mandatoryObjectivesRouteGuardReleasedRef = useRef(true);
  const currentModalRef = useRef(currentModal);
  const objectivesSetupModalConfigRef = useRef(objectivesSetupModalConfig);

  useEffect(() => {
    currentModalRef.current = currentModal;
    objectivesSetupModalConfigRef.current = objectivesSetupModalConfig;
  }, [currentModal, objectivesSetupModalConfig]);

  const isMandatoryAdsObjectivesChromeLocked = useMemo(
    () =>
      isConnectAccountPickerModal(currentModal) ||
      isMandatoryObjectivesSetupModal(currentModal, objectivesSetupModalConfig),
    [currentModal, objectivesSetupModalConfig]
  );

  // Cerrar cualquier modal al cambiar de módulo (Dashboard, Campañas, Objetivos, etc.)
  useEffect(() => {
    if (
      shouldPreserveModalOnRouteChange(
        currentModalRef.current,
        objectivesSetupModalConfigRef.current,
        mandatoryObjectivesRouteGuardReleasedRef.current
      )
    ) {
      return;
    }
    setCurrentModal(MODAL_TYPES.NONE);
    setUpsellModalConfig({});
    setObjectivesSetupModalConfig({});
    setPlansModalMessage('');
    setIsOptimizationsListModalOpen(false);
    setIsOptimizationDetailModalOpen(false);
    mandatoryObjectivesRouteGuardReleasedRef.current = true;
  }, [location.pathname]);

  const openModal = (modalType, config = {}) => {
    setCurrentModal(modalType);
    if (modalType === MODAL_TYPES.UPSELL) {
      setUpsellModalConfig(config);
    }
    if (modalType === MODAL_TYPES.CREATE_OBJECTIVES_SETUP) {
      setObjectivesSetupModalConfig(config);
      mandatoryObjectivesRouteGuardReleasedRef.current = !config.mandatoryUntilGenerated;
    }
  };

  const closeModal = () => {
    setCurrentModal(MODAL_TYPES.NONE);
    setUpsellModalConfig({});
    setObjectivesSetupModalConfig({});
    mandatoryObjectivesRouteGuardReleasedRef.current = true;
  };

  // Convenience functions for specific modals
  const openUpsellModal = (config = {}) => openModal(MODAL_TYPES.UPSELL, config);
  const openNoAdsConnectedModal = () => openModal(MODAL_TYPES.NO_ADS_CONNECTED);
  const openUpdateBusinessDataModal = () => openModal(MODAL_TYPES.UPDATE_BUSINESS_DATA);
  const openConnectGoogleAdsAccountModal = () => openModal(MODAL_TYPES.CONNECT_GOOGLE_ADS_ACCOUNT);
  const openConnectMetaAdsAccountModal = () => openModal(MODAL_TYPES.CONNECT_META_ADS_ACCOUNT);
  // Toggle: si el modal ya está abierto, cerrar; si no, abrirlo
  const openPlansModal = () => {
    if (currentModal === MODAL_TYPES.PLANS) {
      closeModal();
    } else {
      openModal(MODAL_TYPES.PLANS);
    }
  };
  const openConnectionsModal = () => {
    if (currentModal === MODAL_TYPES.CONNECTIONS) {
      closeModal();
    } else {
      openModal(MODAL_TYPES.CONNECTIONS);
    }
  };
  const openCreateObjectivesSetupModal = (config = {}) => openModal(MODAL_TYPES.CREATE_OBJECTIVES_SETUP, config);

  return (
    <ModalsContext.Provider
      value={{
        // Current modal state
        currentModal,
        openModal,
        closeModal,
        
        // Convenience functions
        openUpsellModal,
        openNoAdsConnectedModal,
        openUpdateBusinessDataModal,

        // Google Ads modals
        openConnectGoogleAdsAccountModal,

        // Meta Ads modals
        openConnectMetaAdsAccountModal,

        openPlansModal,
        openConnectionsModal,
        openCreateObjectivesSetupModal,

        // Upsell modal specific
        upsellModalConfig,

        // Create objectives setup (post-connect)
        objectivesSetupModalConfig,

        // Plans modal specific
        plansModalMessage,
        setPlansModalMessage,

        // Optimizations list modal (para blur del brand selector)
        isOptimizationsListModalOpen,
        setIsOptimizationsListModalOpen,
        // Optimizations detail modal (para blur del brand selector)
        isOptimizationDetailModalOpen,
        setIsOptimizationDetailModalOpen,

        isMandatoryAdsObjectivesChromeLocked,
      }}
    >
      {children}
    </ModalsContext.Provider>
  );
};

AdsModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
